"use client";

import React, { useEffect, useRef, useState } from "react";
import { Server, Cpu, Network } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const vendors = [{
  name: "GRC Frameworks",
  integration: "Automated Governance & Risk Management",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/mikrotik_logo_integration.png"
}, {
  name: "ASM Solutions",
  integration: "Continuous Attack Surface Monitoring",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/ubiquiti_logo_integration.png"
}, {
  name: "CSPM Protocols",
  integration: "Cloud Security Posture Optimization",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/cisco_logo_integration.png"
}, {
  name: "TPRM Systems",
  integration: "Third-Party Risk Assessment & Control",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/cambium_logo_integration.png"
}, {
  name: "Data Privacy",
  integration: "Global Compliance & Sovereignty",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/tplink_logo_integration.png"
}, {
  name: "Identity Access",
  integration: "Zero-Trust Architecture & IAM",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/juniper_logo_integration.png"
}, {
  name: "Network Resilience",
  integration: "Intelligent Threat Detection & Response",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/huawei_logo_integration.png"
}, {
  name: "Threat Intel",
  integration: "Predictive AI-Driven Vulnerability Data",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/mimosa_logo_integration.png"
}, {
  name: "Audit Trails",
  integration: "Immutable Logging & Forensics",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/radwin_logo_integration.png"
}, {
  name: "Regulatory GRC",
  integration: "Cross-Border Regulatory Alignment",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/generic_snmp_logo_integration.png"
}];
export default function HardwareCompatibility() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="131483" id="partnership-ecosystem" className="bg-slate-50 text-slate-900 py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/5 rounded-full blur-[120px] opacity-50" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/noise_texture.png')] opacity-[0.02] mix-blend-overlay" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-sm font-semibold mb-6">
            <Server className="size-4" />
            <span>Security by Design Philosophy</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter mb-6 text-slate-900">
            Integrated Cytrusst Ecosystem
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            Quantificial embeds enterprise-grade cybersecurity into every AI transformation. Through our Cytrusst partnership, 
            we provide an integrated suite of GRC, ASM, and CSPM solutions that ensure your digital engineering is 
            secure, compliant, and resilient by default.
          </p>
        </div>

        <div ref={gridReveal.ref} className={`transition-all duration-1000 delay-200 ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <TooltipProvider delayDuration={100}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {vendors.map((vendor, i) => <div key={vendor.name} data-index={i}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="group relative flex flex-col items-center justify-center p-6 h-36 rounded-xl border border-slate-200 bg-white/60 backdrop-blur-lg hover:bg-white/90 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-500 cursor-crosshair overflow-hidden">
                        {}
                        <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-emerald-400/0 group-hover:border-emerald-400/50 transition-colors duration-300" />
                        <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-blue-400/0 group-hover:border-blue-400/50 transition-colors duration-300" />
                        
                        <img src={vendor.logo} alt={`${vendor.name} infrastructure`} className="max-h-12 max-w-[140px] object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-105" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="border-slate-200 bg-white/95 backdrop-blur-md shadow-2xl px-4 py-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Cpu className="size-3.5 text-blue-600" />
                        <p className="font-bold text-sm text-slate-900">
                          {vendor.name}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 font-mono">
                        {vendor.integration}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>)}
            </div>
          </TooltipProvider>
        </div>

        <div className="mt-16 text-center">
          <Button asChild variant="outline" className="border-slate-300 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 transition-colors rounded-md font-semibold">
            <Link to="/ServicesPage">
              <Network className="size-4 mr-2" />
              Explore Security Framework
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}